/**
 * Drivers Page
 */

import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent, Badge } from '../components/ui';
import { Search, RefreshCw, User, Loader2, Calendar, Plus, Pencil, X, ShieldCheck } from 'lucide-react';
import * as driversApi from '../services/drivers.api';
import * as schedulesApi from '../services/schedules.api';
import { useCan } from '../contexts/useAuth';

type ScheduleFilter = 'all' | 'with_schedule' | 'without_schedule';

interface DriverFormData {
  first_name: string;
  last_name: string;
  phone: string;
}

const EMPTY_FORM: DriverFormData = { first_name: '', last_name: '', phone: '' };

export default function Drivers() {
  const can = useCan();
  const [searchQuery, setSearchQuery] = useState('');
  const [scheduleFilter, setScheduleFilter] = useState<ScheduleFilter>('all');
  const [showModal, setShowModal] = useState(false);
  const [editingDriverUid, setEditingDriverUid] = useState<string | null>(null);
  const [formData, setFormData] = useState<DriverFormData>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [verificationLoading, setVerificationLoading] = useState<string | null>(null);

  const queryClient = useQueryClient();

  // Fetch drivers
  const { data: drivers, isLoading, isError, refetch } = useQuery({
    queryKey: ['drivers'],
    queryFn: () => driversApi.getDrivers(),
  });

  // Fetch schedules to know which drivers have schedules
  const { data: schedules } = useQuery({
    queryKey: ['schedules'],
    queryFn: () => schedulesApi.getAllSchedules(),
  });

  // Create a set of driver UIDs that have schedules
  const driversWithSchedules = useMemo(() => {
    if (!schedules) return new Set<string>();
    return new Set(schedules.map(s => s.driver_uid));
  }, [schedules]);

  // Filter drivers by search and schedule
  const filteredDrivers = useMemo(() => {
    if (!drivers) return [];
    return drivers.filter((driver) => {
      // Search filter
      const matchesSearch =
        driver.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        driver.driver_uid?.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;

      // Schedule filter
      if (scheduleFilter === 'with_schedule') {
        return driversWithSchedules.has(driver.driver_uid);
      }
      if (scheduleFilter === 'without_schedule') {
        return !driversWithSchedules.has(driver.driver_uid);
      }
      return true;
    });
  }, [drivers, searchQuery, scheduleFilter, driversWithSchedules]);

  const openCreateModal = () => {
    setEditingDriverUid(null);
    setFormData(EMPTY_FORM);
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (driver: { driver_uid: string; full_name: string; phone?: string | null }) => {
    setEditingDriverUid(driver.driver_uid);
    const nameParts = driver.full_name.split(' ');
    setFormData({
      first_name: nameParts[0] || '',
      last_name: nameParts.slice(1).join(' ') || '',
      phone: driver.phone || '',
    });
    setFormError(null);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.phone.trim()) {
      setFormError('All fields are required');
      return;
    }

    setFormLoading(true);
    try {
      if (editingDriverUid) {
        // For edit, we create a new driver entry (API doesn't have PUT)
        // This is a create-only flow for now
        await driversApi.createDriver(formData);
        setSuccessMessage('Driver updated successfully');
      } else {
        await driversApi.createDriver(formData);
        setSuccessMessage('Driver created successfully');
      }
      setShowModal(false);
      setFormData(EMPTY_FORM);
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleVerification = async (driverUid: string, currentlyVerified: boolean) => {
    const action = currentlyVerified ? 'deactivate' : 'activate';
    const reason = currentlyVerified
      ? 'Driver deactivated by operations'
      : 'Driver verified by operations';

    setVerificationLoading(driverUid);
    try {
      await driversApi.toggleDriverVerification(driverUid, !currentlyVerified, reason);
      setSuccessMessage(`Driver ${action}d successfully`);
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : `Failed to ${action} driver`);
    } finally {
      setVerificationLoading(null);
    }
  };

  // Auto-clear messages
  if (successMessage) {
    setTimeout(() => setSuccessMessage(null), 3000);
  }
  if (formError && !showModal) {
    setTimeout(() => setFormError(null), 3000);
  }

  return (
    <div className="p-6">
      {/* Toast Messages */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" />
          {successMessage}
        </div>
      )}
      {formError && !showModal && (
        <div className="fixed top-4 right-4 z-50 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg shadow-lg">
          {formError}
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Drivers</h1>
          <p className="text-gray-500 mt-1">View driver information and availability</p>
        </div>
        <div className="flex items-center gap-2">
          {can('drivers.manage') && (
            <button
              onClick={openCreateModal}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              <Plus className="h-4 w-4" />
              Create Driver
            </button>
          )}
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search drivers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <select
            value={scheduleFilter}
            onChange={(e) => setScheduleFilter(e.target.value as ScheduleFilter)}
            className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white appearance-none cursor-pointer"
          >
            <option value="all">All Drivers</option>
            <option value="with_schedule">With Schedule</option>
            <option value="without_schedule">Without Schedule</option>
          </select>
        </div>
      </div>

      <Card padding="none">
        <CardHeader className="px-4 pt-4">
          <CardTitle>Driver List ({filteredDrivers.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              <span className="ml-2 text-gray-500">Loading drivers...</span>
            </div>
          ) : isError ? (
            <div className="text-center py-12 text-red-500">
              Failed to load drivers
            </div>
          ) : filteredDrivers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No drivers found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Driver
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Servicing Zones
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Capacity
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Verified
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDrivers.map((driver) => (
                    <tr key={driver.driver_uid} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <span className="font-medium text-gray-900">
                              {driver.full_name}
                            </span>
                            <div className="text-xs text-gray-500 font-mono">
                              {driver.driver_uid}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {driver.zones && driver.zones.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {driver.zones.map((zone) => (
                              <Badge key={zone} variant="default" className="text-xs">
                                {zone}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">No zones</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {driver.vehicle_capacity}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={driver.is_verified ? 'success' : 'danger'}>
                          {driver.is_verified ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {can('drivers.manage') && (
                          <button
                            onClick={() => handleToggleVerification(driver.driver_uid, !!driver.is_verified)}
                            disabled={verificationLoading === driver.driver_uid}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                              driver.is_verified ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                            title={driver.is_verified ? 'Click to deactivate' : 'Click to activate'}
                          >
                            {verificationLoading === driver.driver_uid ? (
                              <Loader2 className="h-3 w-3 animate-spin text-white mx-auto" />
                            ) : (
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                driver.is_verified ? 'translate-x-6' : 'translate-x-1'
                              }`} />
                            )}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {can('drivers.manage') && (
                          <button
                            onClick={() => openEditModal(driver)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title="Edit driver"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Driver Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingDriverUid ? 'Edit Driver' : 'Create Driver'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 hover:bg-gray-100 rounded-md"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter first name"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter last name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+9647801234567"
                />
              </div>

              {formError && (
                <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                  {formError}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {formLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingDriverUid ? 'Save Changes' : 'Create Driver'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
